import { BackendHttpService } from "../../../services/backendHttpService";
import {
  CanonicalExpansionResult,
  ResolvedProfileTechnologies,
  TechnologyReportAllIdsRequest,
  TechnologyReportPagedResult,
  TechnologyReportSearchRequest,
} from "../models/technologyReport.model";

class TechnologyReportService extends BackendHttpService {
  constructor() {
    super("api/reports/technology");
  }

  search(request: TechnologyReportSearchRequest): Promise<TechnologyReportPagedResult> {
    return this.post<TechnologyReportSearchRequest, TechnologyReportPagedResult>(request, "/search");
  }

  /** Every matching PersonId, unpaged — backs "Export CV for all matching." */
  getMatchingPersonIds(request: TechnologyReportAllIdsRequest): Promise<number[]> {
    return this.post<TechnologyReportAllIdsRequest, number[]>(request, "/search/all-ids");
  }

  /** The full matching set (not just the current page) as an .xlsx or .csv file. */
  async exportFile(request: TechnologyReportAllIdsRequest, format: "xlsx" | "csv"): Promise<Blob> {
    const accessToken = await this.getAccessToken();

    const response = await fetch(this.buildUrl(`/export?format=${format}`), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }
}

class TechnologyGraphService extends BackendHttpService {
  constructor() {
    super("api/technology-graph");
  }

  /**
   * BFS expansion of a canonical node — returns matched canonical names + raw Technology IDs.
   *
   * EXPERIMENT: comment/uncomment entries in EXPANSION_RELATION_TYPES below to control which
   * graph edges are followed. The preview in the report will show the resulting technology names.
   *   VERSION_OF — specific versions/releases of this tech (e.g. ".NET 8" under ".NET Platform")
   *   REQUIRES   — hard dependencies (e.g. "ASP.NET Core" pulls in "C#")
   *   IMPLIES    — strongly implied knowledge (e.g. "EF Core" implies "LINQ")
   *   PART_OF    — ecosystem components (e.g. "Azure Functions" under "Azure")
   *   SUPERSEDES — legacy/replaced techs (e.g. "MAUI" supersedes "Xamarin")
   *   RELATED_TO — commonly co-used, broad (bidirectional, tends to expand a lot)
   */
  expandCanonical(canonicalId: number): Promise<CanonicalExpansionResult> {
    const EXPANSION_RELATION_TYPES = [
      "VERSION_OF",
      //"REQUIRES",
      //"IMPLIES",
      //"PART_OF",
      // "SUPERSEDES",
      // "RELATED_TO",
    ];

    const params = new URLSearchParams({ maxDepth: "1" });
    EXPANSION_RELATION_TYPES.forEach((rt) => params.append("relationTypes", rt));
    return this.get<CanonicalExpansionResult>(`/canonicals/${canonicalId}/expand?${params.toString()}`);
  }
}

class PositionProfileResolveService extends BackendHttpService {
  constructor() {
    super("api/position-profiles");
  }

  /** Dynamically resolves all technology IDs for a profile via BFS traversal. */
  resolveProfileTechnologies(profileId: number): Promise<ResolvedProfileTechnologies> {
    return this.get<ResolvedProfileTechnologies>(`/${profileId}/resolve-technologies`);
  }
}

export const technologyReportService = new TechnologyReportService();
export const technologyGraphService = new TechnologyGraphService();
export const positionProfileResolveService = new PositionProfileResolveService();
